<template>
  <div class="calendar-month">
    <WholeCalendarToolbar :date-moment="dateMoment" @date-change="$emit('date-change', $event)" />
    <div class="calendar-table">
      <div class="column-header">
        <div
          v-for="(weekdayNumber, weekdayIndex) in _sortedWeekdayNumbers"
          :key="weekdayIndex"
          class="column-header-item"
        >
          {{ $_getWeekdayTextByNumber(weekdayNumber) }}
        </div>
      </div>
      <div
        v-for="(rowData, rowIndex) in _calendarDataArray"
        :key="rowIndex"
        class="calendar-table-row"
      >
        <div
          v-for="(columnData, columnIndex) in rowData"
          :key="columnIndex"
          ref="columnData"
          class="row-item"
          :class="[
            { today: $_checkSameDay(columnData.date, _todayMoment) },
            { 'in-month': columnData.date.get('month') == dateMoment.get('month') },
          ]"
        >
          <div class="day-number-sec">
            <p>{{ columnData.date.get('date') }}</p>
          </div>
          <div v-if="columnData.events" class="day-events">
            <div
              v-for="(event, eventIndex) in $_getDisplayEvents(columnData.events)"
              :key="eventIndex"
              class="event"
              @click="$_handleEventClick(event)"
            >
              <img
                v-if="$_getEventTypeIcon(event.eventType)"
                class="icon"
                :src="$_getEventTypeIcon(event.eventType)"
                alt=""
              />
              <div class="name">
                {{ event.name }}
              </div>
            </div>
          </div>
          <div
            v-if="$_getAppendEvents(columnData.events)"
            class="more-button"
            @click.stop="$_focusDateSet(columnData.date)"
          >
            +
            {{ $t('{number}項', { number: $_getAppendEvents(columnData.events).length }) }}
          </div>
        </div>
      </div>
    </div>
    <WholeCalendarDayEventsDialog
      ref="dayEventDialog"
      :title="_focusDateTitle"
      :event-types="eventTypes"
      :events="_focusDateEvents"
      :position="_dayEventDialogPosition"
      @event-click="$_handleEventClick($event)"
    />
  </div>
</template>

<script>
import moment from 'moment';
export default {
  name: 'WholeCalendarMonth',
  props: {
    eventTypes: Array,
    weekdaysName: Array,
    weekdayNumbers: Array,
    locale: String,
    dateMoment: Object,
    events: Array,
  },
  data: () => ({
    dayFormat: 'YYYY-MM-DD',
    maxEventCountInDay: 3,
    focusDate: null,
  }),
  computed: {
    _focusDataIndex() {
      if (!this.focusDate) {
        return -1;
      }
      let _focusDataIndex = -1;
      this._eventInAllArray.forEach((event, eventIndex) => {
        if (moment(event.date).isSame(moment(this.focusDate))) {
          _focusDataIndex = eventIndex;
        }
      });
      return _focusDataIndex;
    },
    _eventInAllArray() {
      let _eventInAllArray = [];
      const _calendarDataArray = [...this._calendarDataArray];
      _calendarDataArray.forEach(_calendarData => {
        const calendarData = [..._calendarData];
        _eventInAllArray = [..._eventInAllArray, ...calendarData];
      });
      return _eventInAllArray;
    },
    _dayEventDialogPosition() {
      if (!this.focusDate) {
        return null;
      } else {
        const target = this.$refs.columnData[this._focusDataIndex];
        if (!target) {
          return null;
        }
        const rect = target.getBoundingClientRect();
        return {
          x: rect.x + rect.width / 2,
          y: rect.y,
        };
      }
    },
    _focusDateTitle() {
      if (!this.focusDate) {
        return null;
      } else {
        return this.focusDate.locale(this.locale).format(this.$t('M月D日,ddd'));
      }
    },
    _focusDateEvents() {
      if (!this.focusDate) {
        return null;
      } else {
        return this._eventsInDates[this.focusDate.format(this.dayFormat)];
      }
    },
    _sortedEvents() {
      const _sortedEvents = [...this.events];
      _sortedEvents.sort((a, b) => {
        return moment(a.date).diff(moment(b.date));
      });
      return _sortedEvents;
    },
    _eventsInDates() {
      const _eventsInDates = {};
      this._sortedEvents.forEach(_sortedEvent => {
        const tarDateFormat = moment(_sortedEvent.date).format(this.dayFormat);
        if (!(tarDateFormat in _eventsInDates)) {
          _eventsInDates[tarDateFormat] = [];
        }
        _eventsInDates[tarDateFormat].push(_sortedEvent);
      });
      return _eventsInDates;
    },
    _todayMoment() {
      return moment();
    },
    _calendarDataArray() {
      const _calendarDataArray = [];
      let rowIndex = 0;
      for (
        let indexMoment = moment(this._startDayOfCalendarMoment);
        indexMoment.isSameOrBefore(moment(this._endDayOfCalendarMoment));
        indexMoment.add(1, 'day')
      ) {
        if (indexMoment.get('day') == this.weekdayNumbers[0]) {
          _calendarDataArray[rowIndex] = [];
        }
        if (this.weekdayNumbers.includes(indexMoment.get('day'))) {
          _calendarDataArray[rowIndex].push({
            date: moment(indexMoment),
            events: this._eventsInDates[moment(indexMoment).format(this.dayFormat)],
          });
        }
        if (indexMoment.get('day') == this.weekdayNumbers[this.weekdayNumbers.length - 1]) {
          rowIndex++;
        }
      }
      return _calendarDataArray;
    },
    _sortedWeekdayNumbers() {
      const weekdayNumbers = [...this.weekdayNumbers];
      return weekdayNumbers.sort();
    },
    _startDayOfCalendarMoment() {
      return moment(this._startDayOfMonthMoment).startOf('week');
    },
    _startDayOfMonthMoment() {
      return moment(this.dateMoment).startOf('month');
    },
    _endDayOfMonthMoment() {
      return moment(this.dateMoment).endOf('month');
    },
    _endDayOfCalendarMoment() {
      return moment(this._endDayOfMonthMoment).endOf('week');
    },
    _dateMomentMonth() {
      return this.dateMoment.get('month');
    },
  },
  methods: {
    $_getEventTypeIcon(eventType) {
      if (!eventType) {
        return null;
      }
      const tarEventType = this.eventTypes.find(e => {
        return e.name == eventType;
      });
      if (!tarEventType) {
        return null;
      } else {
        return tarEventType.iconImage;
      }
    },
    $_checkSameDay(momentA, momentB) {
      if (momentA.format(this.dayFormat) == momentB.format(this.dayFormat)) {
        return true;
      } else {
        return false;
      }
    },
    $_handleEventClick($event) {
      this.$emit('event-click', $event);
    },
    $_focusDateSet(date) {
      this.focusDate = date;
      this.$refs.dayEventDialog.open();
    },
    $_getAppendEvents(events) {
      if (!events) {
        return null;
      }
      if (events.length > this.maxEventCountInDay) {
        return events.slice(2, events.length);
      } else {
        return null;
      }
    },
    $_getDisplayEvents(events) {
      if (!events) {
        return [];
      }
      if (events.length > this.maxEventCountInDay) {
        return events.slice(0, 2);
      } else {
        return events;
      }
    },
    $_getWeekdayNameByNumber(weekdayNumber) {
      return this.weekdaysName[weekdayNumber];
    },
    $_getWeekdayTextByNumber(weekdayNumber) {
      return this.$t(this.$_getWeekdayNameByNumber(weekdayNumber));
    },
  },
};
</script>
